import React, { useState } from "react";
import Navigation from "./Navigation";
import BackArrow from "./BackArrow";
import Coin from "./Coin";
import { InitialsAvatar, PlayerAvatar, PlayerName, frameClass, avatarLook, avatarSrc, bannerBackground, bannerStyle } from "./PlayerIdentity";
import StatsPreview, { usePlayerStats, MedalsBox, MedalGrid, MedalGridFooter } from "./ProfileStats";
import { t } from "../i18n";
import PageHead from "./PageHead";

const API = process.env.REACT_APP_API_URL || "";

// Changement de mot de passe : une fenêtre superposée plutôt qu'un bloc dans la
// grille — l'écran de profil doit tenir dans la hauteur de l'écran, formulaire
// ouvert ou non.
function PasswordModal({ account, onClose }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [msg, setMsg] = useState({ text: "", error: false });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!current || !next || !confirm) { setMsg({ text: t("profile.fillPasswordFields"), error: true }); return; }
    if (next !== confirm) { setMsg({ text: t("profile.passwordMismatch"), error: true }); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API}/auth/me/password`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${account.token}` },
        body: JSON.stringify({ currentPassword: current, newPassword: next }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setMsg({ text: d.error || t("common.error"), error: true }); setBusy(false); return; }
      setMsg({ text: t("profile.passwordChanged"), error: false });
      setCurrent(""); setNext(""); setConfirm("");
    } catch (_) {
      setMsg({ text: t("common.serverUnreachable"), error: true });
    }
    setBusy(false);
  };

  return (
    <div class="password-modal-overlay" onClick={onClose}>
      <div class="password-modal" onClick={e => e.stopPropagation()}>
        <BackArrow onClick={onClose} />
        <form class="auth-form" onSubmit={submit}>
          <input type="password" placeholder={t("profile.currentPassword")} value={current}
            autoComplete="current-password" autoFocus onChange={e => setCurrent(e.target.value)} />
          <input type="password" placeholder={t("profile.newPassword")} value={next}
            autoComplete="new-password" onChange={e => setNext(e.target.value)} />
          <input type="password" placeholder={t("profile.confirmPassword")} value={confirm}
            autoComplete="new-password" onChange={e => setConfirm(e.target.value)} />
          {msg.text && <p class={msg.error ? "auth-error" : "account-hint"}>{msg.text}</p>}
          <div class="btn-group vertical">
            <button class="btn btn-primary" type="submit" disabled={busy}>{t("profile.changePassword")}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Small camera glyph ("change photo" control) — not a decorative icon.
function CameraGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
      <path d="M9.2 4l-1.2 2H4.5A1.5 1.5 0 003 7.5v11A1.5 1.5 0 004.5 20h15a1.5 1.5 0 001.5-1.5v-11A1.5 1.5 0 0019.5 6h-3.5l-1.2-2z"
        fill="currentColor" />
      <circle cx="12" cy="13" r="3.4" fill="none" stroke="#111826" stroke-width="1.8" />
    </svg>
  );
}

// Flèche qui sort du bac : le geste « envoyer un fichier » de la maquette.
function UploadGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false">
      <path d="M12 15V4m0 0L7.5 8.5M12 4l4.5 4.5M4 14v4.5A1.5 1.5 0 005.5 20h13a1.5 1.5 0 001.5-1.5V14"
        fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
    </svg>
  );
}

// Aperçu du bouton de profil, tel que la barre du haut le montrera : même
// balisage, sous une enveloppe .navigation neutralisée (layout.css) pour que
// chaque habillage le dessine comme le vrai. Le pseudo s'y modifie en place.
// Le nom se modifie dans l'onglet « Profil » ; sans compte, pas d'onglet, il
// reste modifiable directement dans l'aperçu (`editable`).
function ProfilePreview({ player, nameLabel, onNameChange, editable }) {
  const style = bannerStyle(player);
  const src = avatarSrc(player);
  return (
    <div class="navigation profile-preview">
      <div class={`btn Login-button${style ? " has-banner" : ""}`} style={style}>
        {src
          ? <img src={src} alt="" class={frameClass(player).trim()} />
          : <InitialsAvatar name={player.pseudo} size={45} className={`nav-initials${frameClass(player)}`} look={avatarLook(player)} />}
        {editable ? (
          /* Le champ prend la largeur du texte : un double invisible, dans la
             même case de grille, lui donne sa taille. */
          <span class="pid-name-wrap profile-preview-field">
            <span class="profile-preview-sizer" aria-hidden="true">{player.pseudo || nameLabel}</span>
            <input class="pid-name profile-preview-name" type="text" size={1} value={player.pseudo} maxLength={20}
              aria-label={nameLabel} placeholder={nameLabel}
              onChange={e => onNameChange(e.target.value)} />
          </span>
        ) : <PlayerName player={player} />}
      </div>
    </div>
  );
}

// Avatar editing block, shared by both variants.
// Envoyer sa propre photo s'achète : sans l'objet, on montre où il se trouve
// plutôt qu'un bouton qui se ferait refuser.
function AvatarEditor({ picture, name, onUploadAvatar, canUpload, rights, onOpenShop }) {
  // Le sélecteur ne propose que les formats achetés : sans le GIF, il ne se
  // laisse même pas choisir, plutôt que d'être refusé après coup.
  const accept = [
    rights?.image ? "image/png,image/jpeg,image/webp" : "",
    rights?.gif ? "image/gif" : "",
  ].filter(Boolean).join(",");
  return (
    <div class="identity-avatar">
      <div class="identity-avatar-pic">
        {picture
          ? <img src={picture} alt="" />
          : <InitialsAvatar name={name} size="100%" className="identity-initials" />}
        {canUpload && (
          <label for="myImage" class="identity-cam" title={t("profile.changePhoto")}>
            <CameraGlyph />
          </label>
        )}
      </div>
      {canUpload
        ? <input type="file" name="myImage" id="myImage" style={{ display: "none" }}
            accept={accept}
            onChange={e => onUploadAvatar(e.target.files[0])} />
        : onOpenShop && (
            <button type="button" class="identity-photo-hint" onClick={onOpenShop}>
              {t("profile.photoLocked")}
            </button>
          )}
    </div>
  );
}

// Identity screen, two variants:
//  - "setup"   : first-time setup (avatar + in-game name) -> "Play" button
//  - "profile" : the profile page — live preview of the profile button, the
//                inventory (click to wear), wallet and statistics.
// The in-game name (nickname) is deliberately distinct from the login username.
function Profile({
  onOpenFullStats, onOpenFullMedals,
  variant, account, name, picture, cosmetics, onCosmetics,
  onNameChange, onUploadAvatar, avatarRights, canChangePassword = true,
  onSave, onBack, onLogin, onLogout, onLeaveGame, hasOngoingGame,
  onStatsExpired, onProfileClick, onOpenSettings, onOpenShop,
  inGame, isHost, isAdmin, onAdminClick, onReturnToLobby, onCloseLobby,
}) {
  const connected = !!account;
  const isSetup = variant === "setup";
  const nameLabel = connected ? t("profile.gameName") : t("profile.nickname");
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  // Sur écran étroit, un seul panneau à la fois : l'écran ne défile pas.
  const [tab, setTab] = useState("profile");
  // Ce qui est porté, tenu ici pour que l'aperçu change au clic, avant même
  // la réponse du serveur.
  const [equipped, setEquipped] = useState(null);
  const [equipError, setEquipError] = useState("");
  // Sélection de médailles pour l'encadré : même logique que l'équipement,
  // mais son propre tableau côté serveur (jusqu'à cinq, pas un slot unique).
  const [equippedMedals, setEquippedMedals] = useState(null);
  const [medalQuery, setMedalQuery] = useState("");

  // Stats are loaded once: wallet, inventory and the statistics panel.
  const { stats, error } = usePlayerStats(!isSetup && connected ? account : null, onStatsExpired);

  // Droits d'envoi : lus une fois par App, relus au retour de la boutique.
  const rights = avatarRights || { image: false, gif: false };
  const canUpload = rights.image || rights.gif;

  const nav = (
    <Navigation
      player={name ? { pseudo: name, picture, cosmetics } : undefined}
      account={account}
      hasOngoingGame={hasOngoingGame}
      inGame={inGame}
      isHost={isHost}
      isAdmin={isAdmin}
      onProfileClick={onProfileClick}
      onSettingsClick={onOpenSettings}
      onShopClick={onOpenShop}
      onAdminClick={onAdminClick}
      onLogout={onLogout}
      onLeaveGame={onLeaveGame}
      onCloseLobby={onCloseLobby}
    />
  );

  // ── Onboarding variant: initial setup, keeps the "Play" button ─────────────
  if (isSetup) {
    const title = connected ? t("profile.setupTitleAccount") : t("profile.setupTitleAnonymous");
    return (
      <div class="container">
        {nav}
        {/* No way back from the setup that follows a login: there is no
            previous screen to return to. */}
        <PageHead onBack={onBack} />
        <div class="Home">
          <div class="centered-content setup-content">
            <div class="main-title">{title}</div>
            <AvatarEditor picture={picture} name={name} onUploadAvatar={onUploadAvatar}
              canUpload={canUpload} rights={rights} onOpenShop={connected ? onOpenShop : undefined} />
            <div class="field">
              <label class="field-label" for="pseudo-input">{nameLabel}</label>
              <input id="pseudo-input" type="text" placeholder={nameLabel} value={name}
                onChange={e => onNameChange(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter" && name.trim()) onSave(); }} />
            </div>
            {connected && (
              <p class="account-id">{t("profile.loginId", { name: account.username })}</p>
            )}
            <button class="btn btn-primary" disabled={!name.trim()} onClick={onSave}>{t("profile.play")}</button>
          </div>
        </div>
      </div>
    );
  }

  // ── Profile variant ────────────────────────────────────────────────────────
  const cur = stats?.currency || { coin: t("shop.coin"), junk: t("shop.junk") };
  const w = stats?.wallet || { coins: 0, junk: 0 };
  const inventory = stats?.inventory || [];
  const eq = equipped || stats?.equipped || {};
  const itemOf = id => inventory.find(i => i.id === id);
  // Les objets « envoyer sa photo » ne sont pas des avatars à choisir : ils
  // donnent le droit d'envoyer, la photo elle-même est la première case.
  const avatars = inventory.filter(i => i.kind === "avatar" && !i.data?.upload);
  const banners = inventory.filter(i => i.kind === "banner" && !i.data?.upload);
  const frames = inventory.filter(i => i.kind === "frame");
  const nameStyles = inventory.filter(i => i.kind === "color");
  const photoOn = !eq.avatar || !!itemOf(eq.avatar)?.data?.upload;

  const previewPlayer = {
    pseudo: name, picture,
    cosmetics: stats
      ? {
          ...(cosmetics || {}),
          avatar: itemOf(eq.avatar)?.data, banner: itemOf(eq.banner)?.data,
          frame: itemOf(eq.frame)?.data, color: itemOf(eq.color)?.data,
        }
      : cosmetics,
  };

  const equip = async (slot, itemId) => {
    if ((eq[slot] || null) === (itemId || null)) return;
    const prev = eq;
    const next = { ...eq };
    if (itemId) next[slot] = itemId; else delete next[slot];
    setEquipped(next);
    setEquipError("");
    try {
      const r = await fetch(`${API}/shop/equip`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${account.token}` },
        body: JSON.stringify({ slot, itemId: itemId || null }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || t("profile.equipFailed"));
      setEquipped(d.equipped);
      if (onCosmetics) onCosmetics(d.cosmetics || null);
    } catch (e) {
      setEquipped(prev);
      setEquipError(e.message === "Failed to fetch" ? t("common.serverUnreachable") : e.message);
    }
  };

  const medalIds = equippedMedals || stats?.equippedMedals || [];
  const toggleMedal = async (id) => {
    const has = medalIds.includes(id);
    if (!has && medalIds.length >= 5) return;
    const prev = medalIds;
    const next = has ? medalIds.filter(x => x !== id) : [...medalIds, id];
    setEquippedMedals(next);
    try {
      const r = await fetch(`${API}/auth/me/medals/equip`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${account.token}` },
        body: JSON.stringify({ ids: next }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || t("profile.equipFailed"));
      setEquippedMedals(d.equippedMedals || next);
    } catch (_) {
      setEquippedMedals(prev);
    }
  };

  // Envoyer une photo, c'est vouloir la porter : l'avatar acheté s'efface.
  const uploadPhoto = (file) => {
    onUploadAvatar(file);
    if (eq.avatar && !itemOf(eq.avatar)?.data?.upload) equip("avatar", null);
  };
  const accept = [
    rights.image ? "image/png,image/jpeg,image/webp" : "",
    rights.gif ? "image/gif" : "",
  ].filter(Boolean).join(",");

  // L'inventaire : un sélecteur d'onglet pour chaque élément du profil. Les
  // statistiques ont leur propre colonne ; sur écran étroit, elles deviennent
  // un onglet de plus pour que rien ne défile.
  const tabs = [
    { id: "profile", label: t("profile.profileTab") },
    { id: "avatars", label: t("profile.avatars") },
    { id: "banners", label: t("profile.banners") },
    { id: "frames", label: t("profile.frames") },
    { id: "names", label: t("profile.nameTab") },
    { id: "medals", label: t("profile.medals") },
    { id: "stats", label: t("profile.statsTab"), narrowOnly: true },
  ];
  const panel = (id) => `profile-panel pp-${id}${tab === id ? " active" : ""}`;

  return (
    <div class="container">
      {nav}
      <PageHead onBack={onBack} title={t("profile.inventoryTitle")} />
      <div class="Home profile-page">

        {/* Seule une manche en cours appelle une action ici : au salon, il
            n'y a rien à quitter — le menu du profil s'en charge. */}
        {inGame && (
          <div class="profile-game-card">
            <span class="profile-game-title">{t("profile.gameRunning")}</span>
            <p class="profile-game-hint">{t("profile.returnHint")}</p>
            <div class="profile-game-actions">
              <button class="btn" onClick={onReturnToLobby}>{t("profile.returnToLobby")}</button>
            </div>
          </div>
        )}

        <div class={`profile-grid${connected ? "" : " anonymous"}`}>
          <div class="pp-side">
            <div class="pp-preview">
              <ProfilePreview player={previewPlayer} nameLabel={nameLabel} onNameChange={onNameChange} editable={!connected} />
            </div>
            {connected && (
              <>
                <MedalsBox medals={stats?.medals} equippedIds={medalIds} onOpenTab={() => setTab("medals")} />
                {/* Boutique fermée (onOpenShop absent) : ni porte-monnaie ni
                    bouton. Les pièces restent en base, on ne les montre plus. */}
                {onOpenShop && (<>
                <div class="profile-coins">
                  <span class="profile-coin coin" title={cur.coin}><Coin size={30} alt={cur.coin} />{w.coins}</span>
                  <span class="profile-coin junk" title={cur.junk}><Coin kind="junk" size={30} alt={cur.junk} />{w.junk}</span>
                </div>
                <button class="btn btn-primary profile-shop-btn" onClick={onOpenShop}>{t("profile.shopButton")}</button>
                </>)}
              </>
            )}
          </div>

          {connected ? (
            <>
              <nav class="shop-tabs profile-tabs" aria-label={t("profile.inventoryTitle")}>
                {tabs.map(tb => (
                  <button key={tb.id} type="button"
                    class={`shop-tab profile-tab${tab === tb.id ? " active" : ""}${tb.narrowOnly ? " narrow-only" : ""}`}
                    onClick={() => setTab(tb.id)}>{tb.label}</button>
                ))}
              </nav>

              <section class={panel("profile")}>
                <h3 class="profile-panel-title">{t("profile.profileTab")}</h3>
                <div class="field">
                  <label class="field-label" for="pp-name">{nameLabel}</label>
                  <input id="pp-name" type="text" maxLength={20} placeholder={nameLabel} value={name}
                    onChange={e => onNameChange(e.target.value)} />
                </div>
                <p class="account-id">{t("profile.loginId", { name: account.username })}</p>
                {canChangePassword && (
                  <button type="button" class="btn profile-password-btn" onClick={() => setShowPasswordModal(true)}>
                    {t("profile.changePassword")}
                  </button>
                )}
              </section>

              <section class={panel("avatars")}>
                {canUpload ? (
                  <label class="profile-upload" for="pp-avatar-file">
                    <UploadGlyph />{t("profile.uploadAvatar")}
                    <input type="file" id="pp-avatar-file" accept={accept} style={{ display: "none" }}
                      onChange={e => uploadPhoto(e.target.files[0])} />
                  </label>
                ) : (
                  <button type="button" class="profile-upload off" title={t("profile.photoLocked")} onClick={onOpenShop}>
                    <UploadGlyph />{t("profile.uploadAvatar")}
                  </button>
                )}
                <h3 class="profile-panel-title">{t("profile.avatarList")}</h3>
                <div class="profile-tiles">
                  <button type="button" class={`profile-tile${photoOn ? " on" : ""}`} title={t("profile.myPhoto")}
                    aria-pressed={photoOn} onClick={() => equip("avatar", null)}>
                    {picture ? <img src={picture} alt="" /> : <InitialsAvatar name={name} size={48} />}
                  </button>
                  {avatars.map(a => (
                    <button key={a.id} type="button" class={`profile-tile${eq.avatar === a.id ? " on" : ""}`}
                      title={a.name} aria-pressed={eq.avatar === a.id} onClick={() => equip("avatar", a.id)}>
                      {a.data.look
                        ? <InitialsAvatar name={name} size={48} look={a.data.look} />
                        : <img src={a.data.image} alt={a.name} />}
                    </button>
                  ))}
                </div>
                {equipError && <p class="auth-error">{equipError}</p>}
              </section>

              <section class={panel("banners")}>
                <button type="button" class="profile-upload off" disabled title={t("profile.soon")}>
                  <UploadGlyph />{t("profile.uploadBanner")}
                </button>
                <h3 class="profile-panel-title">{t("profile.bannerList")}</h3>
                <div class="profile-banners">
                  <button type="button" class={`profile-banner${!eq.banner ? " on" : ""}`}
                    aria-pressed={!eq.banner} onClick={() => equip("banner", null)}>
                    <span class="profile-banner-art none" />
                    <span class="profile-banner-name">{t("profile.noBanner")}</span>
                  </button>
                  {banners.map(b => (
                    <button key={b.id} type="button" class={`profile-banner${eq.banner === b.id ? " on" : ""}`}
                      aria-pressed={eq.banner === b.id} onClick={() => equip("banner", b.id)}>
                      <span class="profile-banner-art"
                        style={{ ...bannerBackground(b.data), boxShadow: b.data.ring }} />
                      <span class="profile-banner-name">{b.name}</span>
                    </button>
                  ))}
                  {stats && banners.length === 0 && <p class="profile-soon">{t("profile.emptyBanners")}</p>}
                </div>
              </section>

              <section class={panel("frames")}>
                <h3 class="profile-panel-title">{t("profile.frameList")}</h3>
                <div class="profile-tiles">
                  <button type="button" class={`profile-tile${!eq.frame ? " on" : ""}`} title={t("profile.noFrame")}
                    aria-pressed={!eq.frame} onClick={() => equip("frame", null)}>
                    <InitialsAvatar name={name} size={48} />
                  </button>
                  {frames.map(f => (
                    <button key={f.id} type="button" class={`profile-tile${eq.frame === f.id ? " on" : ""}`}
                      title={f.name} aria-pressed={eq.frame === f.id} onClick={() => equip("frame", f.id)}>
                      <PlayerAvatar player={{ pseudo: name, cosmetics: { frame: f.data } }} size={48} />
                    </button>
                  ))}
                </div>
                {stats && frames.length === 0 && <p class="profile-soon">{t("profile.emptyFrames")}</p>}
              </section>

              {/* Les styles de pseudo : des cases comme les avatars, le pseudo
                  du joueur écrit dedans dans chaque parure. */}
              <section class={panel("names")}>
                <h3 class="profile-panel-title">{t("profile.nameList")}</h3>
                <div class="profile-tiles">
                  <button type="button" class={`profile-tile name-tile${!eq.color ? " on" : ""}`}
                    title={t("profile.noNameStyle")} aria-pressed={!eq.color} onClick={() => equip("color", null)}>
                    <span class="pid-name">{name}</span>
                  </button>
                  {nameStyles.map(n => (
                    <button key={n.id} type="button" class={`profile-tile name-tile${eq.color === n.id ? " on" : ""}`}
                      title={n.name} aria-pressed={eq.color === n.id} onClick={() => equip("color", n.id)}>
                      <span class={`pid-name pn-look pn-look-${n.data.look}`}>{name}</span>
                    </button>
                  ))}
                </div>
                {stats && nameStyles.length === 0 && <p class="profile-soon">{t("profile.emptyNameStyles")}</p>}
              </section>

              <section class={panel("medals")}>
                <h3 class="profile-panel-title">{t("profile.medals")}</h3>
                <p class="medal-pick-hint">{t("medals.pickHint")}</p>
                <MedalGrid medals={stats?.medals || []} query={medalQuery} equippedIds={medalIds} onToggleEquip={toggleMedal} badgeSize={60} />
                <MedalGridFooter query={medalQuery} onQuery={setMedalQuery} onOpenFull={onOpenFullMedals} />
              </section>

              <section class={`profile-panel pp-stats${tab === "stats" ? " active" : ""}`}>
                {error ? <p class="auth-error">{error}</p>
                       : <StatsPreview stats={stats} onOpenFull={onOpenFullStats} />}
              </section>
            </>
          ) : (
            <section class="profile-panel account-cta active">
              <h2 class="profile-panel-title">{t("profile.ctaTitle")}</h2>
              <p class="account-hint">{t("profile.ctaText")}</p>
              <button class="btn btn-primary" onClick={onLogin}>{t("profile.ctaButton")}</button>
            </section>
          )}
        </div>
      </div>
      {showPasswordModal && <PasswordModal account={account} onClose={() => setShowPasswordModal(false)} />}
    </div>
  );
}

export default Profile;
